import {Actions, DropdownActionMenuItem, DropdownItem} from 'platform/components';
import {Box, HStack, useElementSize} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ReactNode, useState} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {EntityHeaderAction} from '../../EntityHeader/types/EntityHeaderAction';
import {ActionFit, getNextActionFit} from '../utils/getNextActionFit';
import {MovableHeaderAction, splitCompactHeaderActions} from '../utils/splitCompactHeaderActions';

const INITIAL_FIT: ActionFit = {overflowCount: 0, requiredWidths: [], decidedAtRowWidth: null};

const getOverflowMenuItem = (action: MovableHeaderAction): DropdownActionMenuItem =>
  match(action)
    .with({type: 'button'}, (buttonAction) => ({
      label: buttonAction.title,
      onClick: buttonAction.onClick,
      isDisabled: buttonAction.isDisabled,
      isLoading: buttonAction.isLoading,
      tooltip: isNotNil(buttonAction.tooltip) ? {label: buttonAction.tooltip} : undefined,
      'data-testid': buttonAction['data-testid'],
    }))
    .with({type: 'buttonWithTooltip'}, (buttonAction) => ({
      label: buttonAction.title,
      onClick: buttonAction.onClick,
      isDisabled: buttonAction.isDisabled,
      isLoading: buttonAction.isLoading,
      tooltip: buttonAction.tooltip,
      'data-testid': buttonAction['data-testid'],
    }))
    .with({type: 'dropdown-button'}, (dropdownAction) => ({
      label: dropdownAction.title ?? '',
      isDisabled: dropdownAction.isDisabled,
      submenuContent: (closeDropdown: () => void) =>
        dropdownAction.menuItems.map((menuItem) => (
          <DropdownItem
            key={`overflowSubmenuItem-${menuItem.label}`}
            label={menuItem.label}
            isDisabled={menuItem.isDisabled}
            onClick={(event) => {
              menuItem.onClick?.(event);
              closeDropdown();
            }}
            data-testid={menuItem['data-testid']}
          />
        )),
      'data-testid': dropdownAction['data-testid'],
    }))
    .exhaustive();

interface CompactHeaderActionsProps extends TestIdProps {
  actions: EntityHeaderAction[] | undefined;
  /** Content before the actions that always stays, such as the price. */
  children?: ReactNode;
}

/**
 * The trailing part of the collapsed record header. Actions that do not fit the row move into the
 * "more" menu, and they come back when the row has room again.
 */
export function CompactHeaderActions(props: CompactHeaderActionsProps) {
  const [availableRef, availableWidth] = useElementSize<HTMLDivElement>();
  const [rowRef, rowWidth] = useElementSize<HTMLDivElement>();
  const [fit, setFit] = useState<ActionFit>(INITIAL_FIT);

  const actions = props.actions ?? [];
  const split = splitCompactHeaderActions(actions, fit.overflowCount);

  // The measured widths decide how many actions move, so the state follows them while rendering.
  const nextFit = getNextActionFit(fit, rowWidth, availableWidth, split.movableCount);
  if (nextFit !== fit) {
    setFit(nextFit);
  }

  const hasPageMenuItems = (split.overflowMenu?.menuItems.length ?? 0) > 0;
  const movedMenuItems = split.overflowActions.map((action, index) => ({
    ...getOverflowMenuItem(action),
    hasSeparator: hasPageMenuItems && index === split.overflowActions.length - 1,
  }));

  const rowActions: EntityHeaderAction[] =
    movedMenuItems.length > 0
      ? [
          {
            type: 'dropdown-iconButton',
            priority: 'secondary',
            ...split.overflowMenu,
            menuItems: [...movedMenuItems, ...(split.overflowMenu?.menuItems ?? [])],
          },
          ...split.shownActions,
        ]
      : actions;

  return (
    <Box ref={availableRef} flexGrow={1} flexShrink={1} flexBasis={0} minWidth={0}>
      <HStack justify="flex-end">
        <Box ref={rowRef} flexShrink={0}>
          <HStack align="center" spacing={3}>
            {props.children}
            <Actions
              actions={rowActions}
              data-testid={suffixTestId('compactHeaderActions', props)}
            />
          </HStack>
        </Box>
      </HStack>
    </Box>
  );
}
